import { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import { CurrencyResponse, getRatesAll } from '../api/currency';
import { currencies } from '../datas';
import {
  saleGlobalId,
  snpSaleState,
  snpSaleEditFormState,
  EditSnPSaleForm,
} from '../state/openSale';
import FOSBSBoolean from '../common/FOSBSBoolean';
import FOSBSDatePicker from '../common/FOSBSDatePicker';
import FOSBSMultiSelect from '../common/FOSBSMultiSelect';
import FOSBSMultiSelectAsync from '../common/FOSBSMultiSelectAsync';
import FOSBSTextAreaInput from '../common/FOSBSTextAreaInput';
import FOSBSTextInput from '../common/FOSBSTextInput';
import FOSBSPriceInput from '../common/FOSBSPriceInput';
import FOSBSVesselFinder from '../common/FOSBSVesselFinder';
import { MultiSelectOptions } from '../common/form-elements';
import {
  convertDbSaleToEditSaleForm,
  convertToSaleEditPayload,
} from '../mapper/sale';
import { toast } from 'react-toastify';
import { modalEditorOpen } from '../state/modals';
import { shallowFormDiff } from '../util';
import { getRecentSale, updateSale, deleteSale } from '../services/fosService';
import { updatePriceUSD } from '../util';

const saleTypes = [
  { label: 'Reported sold', value: 'Reported sold' },
  { label: 'Sold', value: 'Sold' },
  { label: 'Failed sale', value: 'Failed sale' },
  { label: 'Sold for Demolition', value: 'Sold for Demolition' },
  { label: 'Demolition', value: 'Demolition' },
  { label: 'Offers Invited', value: 'Offers Invited' },
  { label: 'Requested Price', value: 'Requested Price' },
  { label: 'Contract Price', value: 'Contract Price' },
  { label: 'NewBuilding', value: 'NewBuilding' },
];

interface Props {
  onRequestClose(): void;
}

const generateSaleEditState = (oldSale: any, newSale: any) => {
  if (oldSale === undefined || newSale === undefined) {
    return null;
  }

  const oldPayload = convertToSaleEditPayload(oldSale);
  const newPayload = convertToSaleEditPayload(newSale);

  const stateChange = shallowFormDiff(oldPayload, newPayload);

  return stateChange;
};

export const EditRecentSaleForm = (props: Props) => {
  const [currenciesMapped, setCurrenciesMapped] = useState<MultiSelectOptions>(
    []
  );
  const [saleId, setSaleGlobalId] = useRecoilState(saleGlobalId);

  const setModalOpen = useSetRecoilState(modalEditorOpen);

  const [currencyRates, setCurrencyRates] = useState<CurrencyResponse[]>([]);
  const [usdPriceCalculatedInfo, setUsdPriceCalculatedInfo] = useState({
    calculated: false,
    interestRateDate: '',
    reason: '',
  });

  const [sale, setSale] = useRecoilState(snpSaleState);
  const [form, setForm] = useRecoilState(snpSaleEditFormState);
  const latestFormState = useRef(form);
  const latestCurrencyRates = useRef(currencyRates);

  const resetSale = useResetRecoilState(snpSaleState);
  const resetForm = useResetRecoilState(snpSaleEditFormState);

  let stateChange = generateSaleEditState(sale, form);
  const untouched = stateChange === undefined || stateChange === null;

  useEffect(() => {
    latestFormState.current = form;
    latestCurrencyRates.current = currencyRates;
  }, [form, currencyRates]);

  useEffect(() => {
    const loadData = async () => {
      const lookupResp = await getRecentSale(saleId);
      const sale = convertDbSaleToEditSaleForm(lookupResp[0]);

      // fetch interest rates
      const currencyRatesResp = await getRatesAll(sale.effectiveDate);
      setCurrencyRates(currencyRatesResp);

      setForm(sale);
      setSale(sale);

      // map regions
      setCurrenciesMapped(
        currencies.map((el) => {
          return {
            label: el,
            value: el,
          };
        })
      );
    };

    loadData();
  }, [saleId]);

  const setFormStateElement = async (field: string, value: any) => {
    const latest = latestFormState.current as EditSnPSaleForm;
    const newForm = {
      ...latest,
      [field]: value,
    };
    setForm(newForm);
    latestFormState.current = newForm;

    // if changed field is effectiveDate and priceLocal is set, recalculate priceUSD
    if (newForm.effectiveDate !== undefined && field === 'effectiveDate') {
      // currency must be set for this to make sense

      // fetch interest rates
      const currencyRatesResp = await getRatesAll(newForm.effectiveDate);
      setCurrencyRates(currencyRatesResp);
      const res = updatePriceUSD(
        newForm.currency?.value,
        newForm.priceLocal,
        currencyRatesResp
      );

      setFormStateElement('priceUSD', res.usd);
      setUsdPriceCalculatedInfo(res.calculatedInfo);
    }

    if (['priceLocal', 'currency'].includes(field)) {
      const res = updatePriceUSD(
        newForm.currency?.value,
        newForm.priceLocal,
        latestCurrencyRates.current
      );

      setFormStateElement('priceUSD', res.usd);
      setUsdPriceCalculatedInfo(res.calculatedInfo);
    }
  };

  const doUpdate = async () => {
    stateChange = generateSaleEditState(sale, latestFormState.current);
    if (stateChange === undefined) return;

    await updateSale(saleId, stateChange);

    toast.success('Sale has been updated');
    resetForm();
    resetSale();
    setModalOpen(false);
  };

  const doDelete = async () => {
    const r = window.confirm('Are you sure you want to delete recent sale?');
    if (!r) return;

    const response = await deleteSale(saleId);

    if (response.status) {
      toast.success('Sale has been deleted');
      resetForm();
      resetSale();
      setModalOpen(false);
    } else {
      toast.error(response.message);
    }
  };

  if (form === undefined) return <div></div>;

  return (
    <>
      <div className='row'>
        <div className='col-sm-12 col-md-6 col-lg-6 bg-light pt-2'>
          <div className='col-md-12'>
            <h5 className='fw-light pt-3 border-bottom border-5 border-secondary pb-1'>
              General Info
            </h5>
          </div>

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Connected Imo
            </label>
            <div className='col'>
              <label htmlFor='inputEmail3' className='col-form-label'>
                {form.imo}
              </label>
            </div>
          </div>

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Vessel Name
            </label>
            <div className='col'>
              <FOSBSVesselFinder
                name={'vessel_name'}
                stateSetter={(name, value) => {
                  setFormStateElement('vesselDetails', value);
                }}
                value={form.vesselDetails}
                isMulti={false}
              />
            </div>
          </div>

          <FOSBSDatePicker
            label={'Effective Date'}
            value={form.effectiveDate}
            stateSetter={(name, value) => {
              setFormStateElement(name, value);
            }}
            name={'effectiveDate'}
          />

          <FOSBSPriceInput
            label={'Price Local'}
            value={form.priceLocal}
            onChange={(val) => {
              setFormStateElement('priceLocal', val);
            }}
          />

          <p>Local price showed in millions</p>

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Currency
            </label>
            <div className='col'>
              <FOSBSMultiSelect
                name='currency'
                value={form.currency}
                options={currenciesMapped}
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(name, value);
                }}
                isMulti={false}
              />
            </div>
          </div>

          <FOSBSPriceInput
            label={'Price USD'}
            prefix={'$'}
            value={form.priceUSD}
            onChange={(val) => {
              setFormStateElement('priceUSD', val);
            }}
          />

          {usdPriceCalculatedInfo.calculated && (
            <>
              <strong>Info:</strong> {usdPriceCalculatedInfo.reason}
              <br />
              {usdPriceCalculatedInfo.interestRateDate !== '' && (
                <>
                  <strong>Using rates from:</strong>{' '}
                  {new Date(
                    usdPriceCalculatedInfo.interestRateDate
                  ).toLocaleDateString()}
                </>
              )}
            </>
          )}

          {!usdPriceCalculatedInfo.calculated &&
            usdPriceCalculatedInfo.reason !== '' && (
              <>
                <strong>INFO:</strong> {usdPriceCalculatedInfo.reason}
              </>
            )}

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Type
            </label>
            <div className='col'>
              <FOSBSMultiSelect
                name='salesType'
                value={form.salesType}
                options={saleTypes}
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(name, value);
                }}
                isMulti={false}
              />
            </div>
          </div>

          <div style={{ paddingTop: '1rem' }}>
            <FOSBSTextAreaInput
              label={'Comments'}
              value={form.comments}
              onChange={(e) => {
                setFormStateElement('comments', e.target.value);
              }}
            />
          </div>
        </div>
        <div className='col-sm-12 col-md-6 col-lg-6 bg-light pt-2'>
          <div className='col-md-12'>
            <h5 className='fw-light pt-3 border-bottom border-5 border-secondary pb-1'>
              Details
            </h5>
          </div>

          <FOSBSTextInput
            label={'Source'}
            value={form.source}
            onChange={(e) => {
              setFormStateElement('source', e.target.value);
            }}
          />

          <FOSBSDatePicker
            label={'Delivery Date'}
            value={form.deliveryDate}
            stateSetter={(name, value) => {
              setFormStateElement(name, value);
            }}
            name={'deliveryDate'}
          />

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Buyer
            </label>
            <div className='col'>
              <FOSBSMultiSelectAsync
                dbFieldName='buyer'
                dbTableName='sales'
                name='buyer'
                isMulti={false}
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(name, value);
                }}
                value={form.buyer}
                isCreatable={true}
              />
            </div>
          </div>

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Seller
            </label>
            <div className='col'>
              <FOSBSMultiSelectAsync
                dbFieldName='seller'
                dbTableName='sales'
                name='seller'
                isMulti={false}
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(name, value);
                }}
                value={form.seller}
                isCreatable={true}
              />
            </div>
          </div>

          <FOSBSBoolean
            label={'P&C'}
            name={'privateAndConfidential'}
            value={form.privateAndConfidential}
            stateSetter={(name: string, value: string) => {
              setFormStateElement(name, value);
            }}
          />
          <FOSBSBoolean
            label={'FOSAS Sales'}
            name={'FOSASSales'}
            value={form.FOSASSales}
            stateSetter={(name: string, value: string) => {
              setFormStateElement(name, value);
            }}
          />

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Updated
            </label>
            <div className='col'>
              <label htmlFor='inputEmail3' className='col-form-label'>
                {moment(form.updated).format('DD.MM.yyyy HH:mm:ss')}
              </label>
            </div>
          </div>

          <div className='row pb-1'>
            <label htmlFor='inputEmail3' className='col-sm-4 col-form-label'>
              Updated by
            </label>
            <div className='col'>
              <label htmlFor='inputEmail3' className='col-form-label'>
                {form.updatedBy}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-sm-6' style={{ textAlign: 'right' }}>
          <hr />
          <button
            type='button'
            className='btn btn-secondary btn'
            style={{ marginRight: 10 }}
            onClick={() => {
              resetForm();
              props.onRequestClose();
            }}
          >
            Cancel
          </button>

          <button
            type='button'
            className='btn btn-success btn'
            disabled={untouched}
            onClick={() => {
              doUpdate();
            }}
          >
            Update
          </button>
        </div>
        <div className='col-sm-6' style={{ textAlign: 'right' }}>
          <hr />
          <button
            type='button'
            className='btn btn-danger btn'
            style={{ marginRight: 10 }}
            onClick={() => {
              doDelete();
            }}
          >
            Delete sale
          </button>
        </div>
      </div>
    </>
  );
};
